import React, { useEffect, useState } from 'react';
import { Select, SelectItem, Selection, Input } from '@nextui-org/react';
import { MarketData, Settlement } from 'common/src/utils/types';
import { useTranslation } from '../../hooks/useTranslation';

interface MarketFilterProps {
  data: MarketData[] | Settlement[];
  selectedMarket: string | string[];
  onSelectionChange: (value: string[]) => void;
  selectionMode?: 'single' | 'multiple';
  label?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  defaultSelectedKeys?: string[];
  defaultSelectAll?: boolean;
  hideAllOption?: boolean;
}

interface MarketOption {
  key: string;
  name: string;
  country: string;
  label: string;
}

interface CountryGroup {
  country: string;
  totalFob: number;
  markets: MarketOption[];
}

// Prefix used for country-level toggle rows. Picked so it can't collide with
// a real market name. When the Select emits a key with this prefix we expand
// it into the set of market keys belonging to that country.
const COUNTRY_KEY_PREFIX = '__country__:';
const MISSING_MARKET = 'Sin Mercado';
const MISSING_COUNTRY = 'Sin País';
const MISSING_MARKET_ALIASES = new Set([MISSING_MARKET, 'No Market']);
const MISSING_COUNTRY_ALIASES = new Set([MISSING_COUNTRY, 'No Country']);

const normalizeMarketKey = (value?: string | null): string => {
  const trimmed = value?.trim();
  if (!trimmed || MISSING_MARKET_ALIASES.has(trimmed)) return MISSING_MARKET;
  return trimmed;
};

const normalizeCountryKey = (value?: string | null): string => {
  const trimmed = value?.trim();
  if (!trimmed || MISSING_COUNTRY_ALIASES.has(trimmed))
    return MISSING_COUNTRY;
  return trimmed;
};

const MarketFilter: React.FC<MarketFilterProps> = ({
  data,
  selectedMarket,
  onSelectionChange,
  selectionMode = 'single',
  label,
  className = 'min-w-32',
  size = 'sm',
  defaultSelectedKeys,
  defaultSelectAll = false,
  hideAllOption = false,
}) => {
  const { t } = useTranslation('common');
  const finalLabel = label || t('Mercado de Venta');
  const [search, setSearch] = useState('');
  const selectedMarketArray = React.useMemo(
    () =>
      Array.isArray(selectedMarket)
        ? selectedMarket.map(normalizeMarketKey)
        : [],
    [selectedMarket]
  );
  const selectedMarketSingle = React.useMemo(
    () =>
      Array.isArray(selectedMarket) ? '' : normalizeMarketKey(selectedMarket),
    [selectedMarket]
  );
  const displayMarketName = React.useCallback(
    (name: string) =>
      normalizeMarketKey(name) === MISSING_MARKET ? t(MISSING_MARKET) : name,
    [t]
  );
  const displayCountryName = React.useCallback(
    (country: string) =>
      normalizeCountryKey(country) === MISSING_COUNTRY
        ? t(MISSING_COUNTRY)
        : country,
    [t]
  );

  const marketOptions: MarketOption[] = React.useMemo(() => {
    const marketMap = new Map<string, { name: string; country: string }>();

    data.forEach((item) => {
      const name = normalizeMarketKey(item.market?.name);
      if (!marketMap.has(name)) {
        marketMap.set(name, {
          name,
          country: normalizeCountryKey(item.market?.country),
        });
      }
    });

    return Array.from(marketMap.values()).map((m) => ({
      key: m.name,
      name: m.name,
      country: m.country,
      label: m.country
        ? `${displayMarketName(m.name)}, ${displayCountryName(m.country)}`
        : displayMarketName(m.name),
    }));
  }, [data, displayCountryName, displayMarketName]);

  const markets = React.useMemo(
    () => marketOptions.map((m) => m.key),
    [marketOptions]
  );

  // Group markets by country; sort countries by total FOB (desc) when the
  // input is Settlement[] (has settlement_details), else alphabetically.
  // Markets inside each country are sorted alphabetically.
  const countryGroups: CountryGroup[] = React.useMemo(() => {
    const fobByCountry = new Map<string, number>();
    if (data.length > 0 && 'settlement_details' in (data[0] as any)) {
      (data as Settlement[]).forEach((s) => {
        const country = normalizeCountryKey(s.market?.country);
        const fob = (s.settlement_details || []).reduce(
          (sum, d) => sum + (d.usd_total_fob || 0),
          0
        );
        fobByCountry.set(country, (fobByCountry.get(country) || 0) + fob);
      });
    }

    const groups = new Map<string, CountryGroup>();
    for (const m of marketOptions) {
      if (!groups.has(m.country)) {
        groups.set(m.country, {
          country: m.country,
          totalFob: fobByCountry.get(m.country) || 0,
          markets: [],
        });
      }
      groups.get(m.country)!.markets.push(m);
    }

    const list = Array.from(groups.values());
    list.forEach((g) =>
      g.markets.sort((a, b) => a.name.localeCompare(b.name))
    );
    const hasFob = Array.from(fobByCountry.values()).some((v) => v > 0);
    list.sort((a, b) =>
      hasFob
        ? b.totalFob - a.totalFob
        : a.country.localeCompare(b.country)
    );
    return list;
  }, [marketOptions, data]);

  // Apply search filter — keep a country if any of its markets match OR the
  // country name itself matches.
  const filteredGroups: CountryGroup[] = React.useMemo(() => {
    if (!search) return countryGroups;
    const lower = search.toLowerCase();
    return countryGroups
      .map((g) => {
        const countryMatch = displayCountryName(g.country)
          .toLowerCase()
          .includes(lower);
        const matchingMarkets = g.markets.filter(
          (m) => m.label.toLowerCase().includes(lower)
        );
        if (countryMatch && matchingMarkets.length === 0) {
          // Country name matches but no market name matched — still show all
          // markets so the user can tell what's inside.
          return g;
        }
        if (matchingMarkets.length > 0) {
          return { ...g, markets: matchingMarkets };
        }
        return null;
      })
      .filter((g): g is CountryGroup => g !== null);
  }, [countryGroups, displayCountryName, search]);

  useEffect(() => {
    if (
      selectionMode === 'multiple' &&
      defaultSelectAll &&
      markets.length > 0
    ) {
      onSelectionChange(markets);
    }
  }, []);

  // Display-side selection: includes synthetic country keys so NextUI shows
  // the country row as checked whenever all of its markets are selected.
  const displaySelectedKeys = React.useMemo(() => {
    if (selectionMode !== 'multiple') {
      return new Set([selectedMarketSingle]);
    }
    const selectedSet = new Set(selectedMarketArray);
    const keys: string[] = Array.from(selectedSet);
    countryGroups.forEach((g) => {
      if (g.markets.length === 0) return;
      if (g.markets.every((m) => selectedSet.has(m.key))) {
        keys.push(COUNTRY_KEY_PREFIX + g.country);
      }
    });
    return new Set(keys);
  }, [selectedMarketArray, selectedMarketSingle, countryGroups, selectionMode]);

  const handleSelectionChange = (keys: Selection) => {
    const nextKeys =
      keys === 'all'
        ? markets
        : Array.from(keys as Set<React.Key>).map(String);

    if (selectionMode !== 'multiple') {
      onSelectionChange(nextKeys.map(normalizeMarketKey));
      return;
    }

    if (nextKeys.includes('all')) {
      if (selectedMarketArray.length > 0) {
        onSelectionChange([]);
      } else {
        onSelectionChange(markets);
      }
      return;
    }

    const previousDisplay = displaySelectedKeys;
    const nextSet = new Set(nextKeys);

    // Find country-key transitions (added vs removed).
    const toggledOnCountries: string[] = [];
    const toggledOffCountries: string[] = [];
    countryGroups.forEach((g) => {
      const k = COUNTRY_KEY_PREFIX + g.country;
      const was = previousDisplay.has(k);
      const is = nextSet.has(k);
      if (!was && is) toggledOnCountries.push(g.country);
      if (was && !is) toggledOffCountries.push(g.country);
    });

    // Start from the real market keys present in the new selection
    // (ignoring all __country__ synthetic keys).
    const realMarketSet = new Set<string>(
      nextKeys
        .filter((k) => !k.startsWith(COUNTRY_KEY_PREFIX))
        .map(normalizeMarketKey)
    );

    // Expand country toggles into their constituent markets.
    toggledOnCountries.forEach((country) => {
      const group = countryGroups.find((g) => g.country === country);
      group?.markets.forEach((m) => realMarketSet.add(m.key));
    });
    toggledOffCountries.forEach((country) => {
      const group = countryGroups.find((g) => g.country === country);
      group?.markets.forEach((m) => realMarketSet.delete(m.key));
    });

    onSelectionChange(Array.from(realMarketSet));
  };

  const labelMap = React.useMemo(() => {
    const map = new Map<string, string>();
    marketOptions.forEach((m) => map.set(m.key, m.label));
    return map;
  }, [marketOptions]);

  if (marketOptions.length === 0) {
    return null;
  }

  // NextUI's Select doesn't support section headers that participate in
  // multi-selection, so we render a flat list where each country has a
  // "header" SelectItem (bold) followed by its markets (indented).
  const children: React.ReactNode[] = [];

  if (!hideAllOption) {
    if (selectionMode === 'single') {
      children.push(
        <SelectItem
          key='all'
          value='all'
          textValue={t('Todos los mercados')}
        >
          {t('Todos los mercados')}
        </SelectItem>
      );
    } else {
      children.push(
        <SelectItem
          key='all'
          value='all'
          textValue={t('Todos los mercados')}
          className='font-medium'
        >
          <div className='flex justify-between items-center'>
            <span>
              {selectedMarketArray.length > 0
                ? t('Deseleccionar todos')
                : t('Seleccionar todos')}
            </span>
          </div>
        </SelectItem>
      );
    }
  }

  filteredGroups.forEach((g) => {
    if (selectionMode === 'multiple') {
      children.push(
        <SelectItem
          key={COUNTRY_KEY_PREFIX + g.country}
          textValue={displayCountryName(g.country)}
          className='font-semibold'
        >
          <span>{displayCountryName(g.country)}</span>
        </SelectItem>
      );
    } else {
      // Single-select mode: show country as an unselectable-looking label
      // (still technically a SelectItem but visually distinct).
      children.push(
        <SelectItem
          key={COUNTRY_KEY_PREFIX + g.country}
          textValue={displayCountryName(g.country)}
          className='font-semibold pointer-events-none opacity-70'
          isReadOnly
        >
          <span>{displayCountryName(g.country)}</span>
        </SelectItem>
      );
    }
    g.markets.forEach((market) => {
      children.push(
        <SelectItem
          key={market.key}
          value={market.key}
          textValue={market.label}
          className='pl-6'
        >
          {displayMarketName(market.name)}
        </SelectItem>
      );
    });
  });

  return (
    <Select
      label={finalLabel}
      selectedKeys={displaySelectedKeys}
      onSelectionChange={handleSelectionChange}
      selectionMode={selectionMode}
      defaultSelectedKeys={
        defaultSelectedKeys &&
        new Set(defaultSelectedKeys.map(normalizeMarketKey))
      }
      className={className}
      size={size}
      aria-label={`${t('Selecciona mercado de venta')}`}
      placeholder={t('Todos los mercados')}
      listboxProps={{
        topContent: (
          <div className='sticky top-0 z-10 bg-content1 px-1 pt-1 pb-1'>
            <Input
              size='sm'
              placeholder={t('Buscar mercado o país...')}
              value={search}
              onValueChange={setSearch}
              isClearable
              onClear={() => setSearch('')}
            />
          </div>
        ),
      }}
      renderValue={() => {
        if (
          selectedMarketArray.length === markets.length &&
          markets.length > 0
        ) {
          return (
            <div className='flex items-center gap-2'>
              {t('Todos los mercados')}
            </div>
          );
        }
        if (
          Array.isArray(selectedMarket) &&
          selectedMarketArray.length === 0
        ) {
          return (
            <div className='flex items-center gap-2 text-default-400'>
              {t('Ninguno')}
            </div>
          );
        }
        if (selectedMarketArray.length > 0) {
          const displayCount = 2;
          const firstMarkets = selectedMarketArray
            .slice(0, displayCount)
            .map((k) => labelMap.get(k) || k);
          const remaining = selectedMarketArray.length - displayCount;

          return (
            <div className='flex items-center gap-2'>
              {firstMarkets.join(', ')}
              {remaining > 0 && ` +${remaining}`}
            </div>
          );
        }
        return null;
      }}
    >
      {children}
    </Select>
  );
};

export default React.memo(MarketFilter);
